import React from "react";

// ─── Skeleton ─────────────────────────────────────────────────────────────────

const SkeletonQuestion = () => (
  <div className="space-y-2 animate-pulse">
    <div className="h-4 w-48 bg-gray-200 rounded" />
    <div className="h-10 w-full bg-gray-200 rounded-lg" />
  </div>
);

// ─── Single Question Renderer ─────────────────────────────────────────────────

const QuestionItem = ({ question, answer, onChange }) => {
  const { questionId, question: qText, answerType } = question;

  // ── Normal Text ──────────────────────────────────────────────────────────
  if (answerType === "Normal_Text") {
    return (
      <div className="space-y-1.5">
        <label className="text-sm font-medium text-gray-700">{qText}</label>
        <div className="relative">
          <input
            type="text"
            value={answer?.answeredByBuyer || ""}
            onChange={(e) =>
              onChange(questionId, qText, e.target.value)
            }
            placeholder="Type your answer here..."
            className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm outline-none focus:border-primaryColor focus:ring-1 focus:ring-primaryColor/20 transition-all"
          />
        </div>
      </div>
    );
  }

  // ── Range ────────────────────────────────────────────────────────────────
  if (answerType === "Range") {
    const parsed = (() => {
      try {
        return JSON.parse(answer?.answeredByBuyer || "{}");
      } catch {
        return {};
      }
    })();

    const updateRange = (field, val) => {
      const updated = { ...parsed, [field]: val };
      onChange(questionId, qText, JSON.stringify(updated));
    };

    return (
      <div className="space-y-1.5">
        <label className="text-sm font-medium text-gray-700">{qText}</label>
        <div className="grid grid-cols-2 gap-3">
          <div className="relative">
            <label className="absolute -top-2 left-3 text-[10px] font-medium text-primaryColor bg-white px-1">
              Min*
            </label>
            <input
              type="text"
              value={parsed.min || ""}
              onChange={(e) => updateRange("min", e.target.value)}
              placeholder="Min"
              className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm outline-none focus:border-primaryColor focus:ring-1 focus:ring-primaryColor/20 transition-all"
            />
          </div>
          <div className="relative">
            <label className="absolute -top-2 left-3 text-[10px] font-medium text-primaryColor bg-white px-1">
              Max*
            </label>
            <input
              type="text"
              value={parsed.max || ""}
              onChange={(e) => updateRange("max", e.target.value)}
              placeholder="Max"
              className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm outline-none focus:border-primaryColor focus:ring-1 focus:ring-primaryColor/20 transition-all"
            />
          </div>
        </div>
      </div>
    );
  }

  // ── Selection (dropdown) ─────────────────────────────────────────────────
  if (answerType === "Selection") {
    // Options embedded in question text as comma-separated after colon, or use options array if present
    const options = question.options || [];

    return (
      <div className="space-y-1.5">
        <label className="text-sm font-medium text-gray-700">{qText}</label>
        <div className="relative">
          <label className="absolute -top-2 left-3 text-[10px] font-medium text-primaryColor bg-white px-1">
            Type Here*
          </label>
          <select
            value={answer?.answeredByBuyer || ""}
            onChange={(e) => onChange(questionId, qText, e.target.value)}
            className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm outline-none focus:border-primaryColor appearance-none bg-white focus:ring-1 focus:ring-primaryColor/20 transition-all"
          >
            <option value="">Select an option</option>
            {options.map((opt, i) => (
              <option key={i} value={opt}>{opt}</option>
            ))}
          </select>
          <svg
            className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none"
            fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>
    );
  }

  return null;
};

// ─── Main QuestionnaireSection ────────────────────────────────────────────────

const QuestionnaireSection = ({ questions, loading, answers, onAnswerChange }) => {
  if (loading) {
    return (
      <div className="space-y-5">
        {Array(3).fill(0).map((_, i) => <SkeletonQuestion key={i} />)}
      </div>
    );
  }

  if (!questions || questions.length === 0) {
    return (
      <div className="flex items-center gap-2 py-3 px- bg-gray-50 border border-gray-200 rounded-lg">
        <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <p className="text-xs text-gray-500">No product details questions available for this variety.</p>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {questions.map((q) => (
        <QuestionItem
          key={q.questionId}
          question={q}
          answer={answers[q.questionId]}
          onChange={onAnswerChange}
        />
      ))}
    </div>
  );
};

export default QuestionnaireSection;